import usePlanLimit from '@master/hooks/service/usePlanLimit/usePlanLimit';
import planLimitConfig, { Limit } from '@master/hooks/service/usePlanLimit/planLimitConfig';
import { computed, ref } from 'vue';
import gql from 'graphql-tag';
import dayjs from 'dayjs';
import { useAllOnlinePackage } from '@models/package/useAllOnlinePackage';
import type { PtiPackageInfo, PtxPackageInfo } from '@models/package/useAllOnlinePackage';
import { useProfileList } from '@models/profile/profileList';
import { useQuery, useResult } from '@vue/apollo-composable';
import { route } from '@router/router';
import store from '@/store';
export interface OriginPackageInfo {
    sid: string;
    ptxPackageId: string;
    ptiPackageId: string;
    ptiFreeTrial: 0 | 1;
    ptxFreeTrial: 0 | 1;
    startTime: number;
    endTime: number;
    resetTime: number;
    ptxPackageBaseConf: BaseConfig;
    ptiPackageBaseConf: BaseConfig;
    commonPrivilege: CommonPrivilege;
    commonPrivilegeUsage: CommonPrivilegeUsage;
    ptxPrivilege: PtxPrivilege;
    ptxPrivilegeUsage: PtxPrivilegeUsage;
    ptiPrivilege: PtiPrivilege;
    ptiPrivilegeUsage: PtiPrivilegeUsage;
    trialEndTime?: number;
    pvRate?: number;
    eventRate?: number;
}

interface PrivilegeConf {
    key: string;
    operator: string;
    packageId: string;
    remarks: string | null;
    value: number;
    valueType: number;
}

interface BaseConfig {
    packageId: string;
    packageName: string;
    alias: string;
    isOnline: boolean;
    area: 'JP' | 'EN';
    originalPrice: Array<{
        packageId: string;
        period: number;
        monthlyOriginalPrice: number;
        buyMode: number;
        area: 'JP' | 'EN';
        taxRate: string;
    }>;
    level: number;
    currency: string;
    product: string;
    /** 'TRIAL' | 'FREE' | 'GROWTH' | 'ENTERPRISE' */
    type: string;
    status: number;
    createTime: number;
    lastModifyTime: number | null;
    privilegeConfList: Array<PrivilegeConf>;
}

interface CommonPrivilege {
    monthlyPV: number;
    monthlyEvents: number;
    collaborateUsers: number;
    pageGroups: number;
    conversions: number;
    dataStorageMonths: number;
    useUserIdentify: boolean;
    useEventProperty: boolean;
    useDataConnection: boolean;
    useCrossDomain: boolean;
    useHttps: boolean;
    useEventTracking: boolean;
}

interface CommonPrivilegeUsage {
    monthlyPV: number;
    monthlyEvents: number;
    ptxCollaborateUsers: number;
    ptiCollaborateUsers: number;
    pageGroups: number;
    ptxConversions: number;
    ptiConversions: number;
}
interface PtxPrivilege {
    monthlyCampaignViewUsers: number;
    campaigns: number;
    userGroups: number;
    usePopupForm: boolean;
    useCampaignAbTest: boolean;
    useCampaignUserGroupAndSession: boolean;
    useCampaignCodeMode: boolean;
    useCampaignJSTrigger: boolean;
    useCampaignHeatmap: boolean;
    useCampaignAI: boolean;
    usePopup: boolean;
    useStickyBar: boolean;
    useInline: boolean;
    useCampaignReport: boolean;
    useCampaignReportCSVDownload: boolean;
    useCampaignSettingPage: boolean;
    usePopupStickyBarTrigger: boolean;
    useCampaignSchedule: boolean;
    useCampaignGoal: boolean;
}

interface PtxPrivilegeUsage {
    campaigns: number;
    monthlyCampaignViewUsers: number;
    userGroups: number;
}

interface PtiPrivilege {
    HeatmapStorageMonths: number;
    heatmaps: number;
    historicalHeatmaps: number;
    useBasicSegmentation: boolean;
    useCampaignManagement: boolean;
    useConversionTracking: boolean;
    useDataInsightsReport: boolean;
    useFunnel: boolean;
    useHeatmap: boolean;
    useHeatmapAbTest: boolean;
    useHeatmapCompetitor: boolean;
    usePageGroup: boolean;
    useRealtimeTracking: boolean;
    useSegmentation: boolean;
    useSharableHeatmap: boolean;
}

interface PtiPrivilegeUsage {
    heatmaps: number;
    historicalHeatmaps: number;
}

const GET_PROFILE_PACKAGE_INFO_QUERY = gql`
    query getProfileNpPackageInfo($profileId: String!) {
        getProfileNpPackageInfo(profileId: $profileId) {
            sid
            ptxPackageId
            ptiPackageId
            packageType
            startTime
            endTime
            resetTime
            ptxPackageBaseConf {
                packageId
                packageName
                alias
                isOnline
                area
                originalPrice {
                    packageId
                    period
                    monthlyOriginalPrice
                    buyMode
                    area
                    taxRate
                }
                level
                currency
                product
                type
                status
                createTime
                lastModifyTime
                privilegeConfList {
                    packageId
                    key
                    value
                    valueType
                    operator
                    remarks
                }
            }
            ptiPackageBaseConf {
                packageId
                packageName
                alias
                isOnline
                area
                originalPrice {
                    packageId
                    period
                    monthlyOriginalPrice
                    buyMode
                    area
                    taxRate
                }
                level
                currency
                product
                type
                status
                createTime
                lastModifyTime
                privilegeConfList {
                    packageId
                    key
                    value
                    valueType
                    operator
                    remarks
                }
            }
            commonPrivilege {
                monthlyPV
                monthlyEvents
                collaborateUsers
                pageGroups
                conversions
                dataStorageMonths
                useUserIdentify
                useEventProperty
                useDataConnection
                useCrossDomain
                useHttps
                useEventTracking
            }
            commonPrivilegeUsage {
                monthlyPV
                monthlyEvents
                ptxCollaborateUsers
                ptiCollaborateUsers
                pageGroups
                ptxConversions
                ptiConversions
            }
            ptxPrivilege {
                monthlyCampaignViewUsers
                campaigns
                userGroups
                usePopupForm
                useCampaignAbTest
                useCampaignUserGroupAndSession
                useCampaignCodeMode
                useCampaignJSTrigger
                useCampaignHeatmap
                useCampaignAI
                usePopup
                useStickyBar
                useInline
                useCampaignReport
                useCampaignReportCSVDownload
                useCampaignSettingPage
                usePopupStickyBarTrigger
                useCampaignSchedule
                useCampaignGoal
            }
            ptxPrivilegeUsage {
                monthlyCampaignViewUsers
                campaigns
                userGroups
            }
            ptiPrivilege {
                heatmaps
                HeatmapStorageMonths
                historicalHeatmaps
                useSegmentation
                useHeatmapCompetitor
                useSharableHeatmap
                useFunnel
                useHeatmapAbTest
                useHeatmap
                useRealtimeTracking
                useDataInsightsReport
                usePageGroup
                useConversionTracking
                useCampaignManagement
                useBasicSegmentation
            }
            ptiPrivilegeUsage {
                heatmaps
                historicalHeatmaps
            }
            ptiFreeTrial
            ptxFreeTrial
            trialEndTime
            bindWay
        }
    }
`;

const packageInfoResult = ref();
const profileNpPackageInfoLoading = ref(false);
const { profileList } = useProfileList();
const { allOnlinePackage } = useAllOnlinePackage();
export function useNPPackageInfo() {
    const getProfileNpPackageInfo = (params?: {
        sid?: string;
        resultCallback?: (T: OriginPackageInfo) => void;
        rejectCallback?: (err: any) => void;
        finallyCallback?: (T: any) => void;
    }) => {
        const profileId = params?.sid || route.value.params?.sid;
        if (!profileId) return;

        profileNpPackageInfoLoading.value = true;
        const { onResult, onError } = useQuery<{ getProfileNpPackageInfo: OriginPackageInfo }>(
            GET_PROFILE_PACKAGE_INFO_QUERY,
            () => {
                return {
                    profileId
                };
            },
            {
                fetchPolicy: 'no-cache'
            }
        );

        onResult(data => {
            if (data?.data?.getProfileNpPackageInfo) {
                packageInfoResult.value = data?.data?.getProfileNpPackageInfo || {};
                params?.resultCallback &&
                    typeof params?.resultCallback === 'function' &&
                    params?.resultCallback(data.data.getProfileNpPackageInfo);
                params?.finallyCallback &&
                    typeof params?.finallyCallback === 'function' &&
                    params?.finallyCallback(data.data.getProfileNpPackageInfo);
            }
            profileNpPackageInfoLoading.value = false;
        });

        onError(err => {
            packageInfoResult.value = undefined;
            profileNpPackageInfoLoading.value = false;
            params?.rejectCallback &&
                typeof params?.rejectCallback === 'function' &&
                params?.rejectCallback(err);
            params?.finallyCallback &&
                typeof params?.finallyCallback === 'function' &&
                params?.finallyCallback(err);
        });
    };

    // 套餐信息
    const profileNpPackageInfo = computed(() => {
        if (!packageInfoResult.value) return undefined;
        const PVRate: number =
            (packageInfoResult.value?.commonPrivilegeUsage?.monthlyPV ?? 0) /
            (packageInfoResult.value.commonPrivilege?.monthlyPV || 0);
        const EventRate: number =
            (packageInfoResult.value.commonPrivilegeUsage?.monthlyEvents ?? 0) /
            (packageInfoResult.value.commonPrivilege?.monthlyEvents ?? 0);
        return {
            ...packageInfoResult.value,
            startTime: dayjs(Number(packageInfoResult.value.startTime)).format('YYYY/MM/DD'),
            endTime: dayjs(Number(packageInfoResult.value.endTime)).format('YYYY/MM/DD'),
            pvRate: Number.isNaN(PVRate) ? 0 : Math.ceil(PVRate * 100),
            eventRate: Number.isNaN(EventRate) ? 0 : Math.ceil(EventRate * 100),
            resetTime: dayjs(Number(packageInfoResult.value.resetTime)).format('YYYY/MM/DD')
            // packageType: profileList.value?.find(item => item.sid === route?.value.params?.sid)
            //     ?.packageType
        };
    });

    // 当前档案套餐信息
    const currentPackageInfo = computed(() => {
        if (!allOnlinePackage.value || !profileNpPackageInfo.value) return undefined;
        const experiencePackageInfo: PtxPackageInfo | undefined = allOnlinePackage.value?.ptx?.find(
            (item: PtxPackageInfo) => item.packageId === profileNpPackageInfo.value?.ptxPackageId
        );
        const insightPackageInfo: PtiPackageInfo | undefined = allOnlinePackage.value?.pti?.find(
            (item: PtxPackageInfo) => item.packageId === profileNpPackageInfo.value?.ptiPackageId
        );
        return {
            insight: insightPackageInfo,
            insightIsGrowth: insightPackageInfo?.type.toLocaleLowerCase() === 'growth',
            insightIsTrial: insightPackageInfo?.type.toLocaleLowerCase() === 'trial',
            experience: experiencePackageInfo,
            experienceIsGrowth: experiencePackageInfo?.type.toLocaleLowerCase() === 'growth',
            experienceIsTrial: experiencePackageInfo?.type.toLocaleLowerCase() === 'trial'
        };
    });

    return {
        currentPackageInfo,
        profileNpPackageInfo,
        profileNpPackageInfoLoading,

        getProfileNpPackageInfo
    };
}

/**
 * 校验套餐项
 * @param limitType
 * @param callback
 */
export function verifyPtxPackage(
    type: keyof typeof planLimitConfig.ptxLimits | keyof typeof planLimitConfig.commonLimits,
    onSuccess?: () => void,
    onFailed?: any,
    isNpPackage: boolean = true
) {
    const { checkAuth, showUpGradeDialog } = usePlanLimit();
    const item =
        (planLimitConfig.commonLimits[type] as Limit) || (planLimitConfig.ptxLimits[type] as Limit);
    if (!item) {
        onSuccess && typeof onSuccess === 'function' && onSuccess();
        return;
    }

    const res = checkAuth({
        limitType: type,
        productType: 'ptx',
        editType: item?.editType,
        isNpPackage
    });
    if (res) {
        if (res?.effective) {
            onSuccess && typeof onSuccess === 'function' && onSuccess();
        } else {
            onFailed && typeof onFailed === 'function'
                ? onFailed(res)
                : showUpGradeDialog({
                      limitType: res.authKey,
                      privilege: res.privilege,
                      product: 'ptx'
                  });
        }
    }
}
